import { Link } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Timer, Gift, Trophy, BarChart3, ArrowRight } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxuZW9uJTIwY2l0eSUyMG5pZ2h0fGVufDB8fHx8MTc4NTk2MjY5NXww&ixlib=rb-4.1.0&q=85";

const bullets = [
  { icon: Timer, title: "1 minute = 1 point", body: "Your session time streams straight into the portal while you play. No forms, no claims." },
  { icon: Gift, title: "Cash, cars, crates", body: "Burn points on in-game cash, vehicles, food crates, plates and VIP perks." },
  { icon: Trophy, title: "10 loyalty ranks", body: "Rookie to Liquid Elite. Every rank unlocks better perks and cheaper rewards." },
  { icon: BarChart3, title: "Achievements & drops", body: "Hit milestones for bonus points and catch limited multiplier weekends." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Brand />
          <Link to="/login" data-testid="landing-login-link">
            <Button className="rounded-none" data-testid="landing-login-btn">
              Player Login
            </Button>
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-border">
        <img src={HERO} alt="Neon city night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/80" />
        <div className="absolute inset-0 drip" />
        <div className="relative mx-auto max-w-7xl px-6 py-28 md:py-40">
          <div className="rise max-w-3xl">
            <p className="mb-6 text-xs uppercase tracking-[0.3em] text-primary">
              Create · Roleplay · Belong
            </p>
            <h1 className="text-4xl font-bold leading-[0.95] sm:text-5xl lg:text-6xl">
              Your time in the city
              <br />
              <span className="text-primary">finally pays out.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base text-muted-foreground">
              LiquidRP Loyalty tracks every minute you roleplay on our GTA RP server and converts it
              into points you can spend on real in-game perks.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/login">
                <Button size="lg" className="group rounded-none px-8" data-testid="hero-cta">
                  Start earning
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-none px-8" data-testid="hero-secondary-cta">
                  I already play here
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="hex-grid border-b border-border">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-24 md:grid-cols-12">
          {bullets.map((b, i) => (
            <div
              key={b.title}
              data-testid={`feature-card-${i}`}
              className={`card-hover border border-border bg-card p-8 ${i % 3 === 0 ? "md:col-span-7" : "md:col-span-5"}`}
            >
              <b.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
              <h3 className="mt-6 text-lg font-semibold">{b.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-12 text-xs uppercase tracking-[0.2em] text-muted-foreground md:flex-row md:items-center md:justify-between">
        <Brand />
        <div className="flex flex-wrap items-center gap-6">
          <span>© {new Date().getFullYear()} LiquidRP — Loyalty Portal</span>
          <span className="text-[10px] tracking-[0.2em] text-muted-foreground/70" data-testid="footer-powered-by">
            Powered by Ayo
          </span>
          <Link
            to="/login?staff=1"
            data-testid="footer-staff-login-link"
            className="text-[10px] tracking-[0.2em] text-muted-foreground/70 transition-colors hover:text-primary"
          >
            Staff login
          </Link>
        </div>
      </footer>
    </div>
  );
}
